import Cookies from 'js-cookie';
import { apiFetch } from '../../services/api';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001'}/admin`;

const ClinicService = {
  // Add a new clinic
  async addClinic(clinicData, files) {
    // Since files now contains URLs (strings) instead of File objects,
    // we can combine them with clinicData and send as JSON
    const payload = {
      ...clinicData,
      profilePath: files.profilePhoto || undefined,
      licencePath: files.license || undefined,
      authorizedSign: files.authorizedSignature || undefined,
      commercialLicence: files.commercialLicense || undefined,
    };

    // Debug payload
    console.log('Payload for addClinic:', JSON.stringify(payload, null, 2));

    try {
      const response = await apiFetch(`${API_BASE_URL}/addClinicRegistration`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add clinic: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error adding clinic: ${error.message}`);
    }
  },

  // Update an existing clinic
  async updateClinic(clinicId, clinicData, files) {
    // Combine existing or new file URLs with clinicData
    const payload = {
      ...clinicData,
      profilePath: files.profilePhoto || clinicData.profilePath || '',
      licencePath: files.license || clinicData.licencePath || '',
      authorizedSign: files.authorizedSignature || clinicData.authorizedSign || '',
      commercialLicence: files.commercialLicense || clinicData.commercialLicence || '',
    };

    // Debug payload
    console.log('Payload for updateClinic:', JSON.stringify(payload, null, 2));

    try {
      const response = await apiFetch(`${API_BASE_URL}/updateClinic/${clinicId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update clinic: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error updating clinic: ${error.message}`);
    }
  },

  async updatePaymentStatus(clinicCount) {
    // Combine existing or new file URLs with clinicData
    const payload = {
      clinic_limit: clinicCount,
      adminId: Cookies.get('adminid'),
    };

    try {
      const response = await apiFetch(`${API_BASE_URL}/updateAdminPaymentStatus`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update clinic: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error updating clinic: ${error.message}`);
    }
  },
  // Delete a clinic
  async deleteClinic(clinicId) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/deleteClinic/${clinicId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete clinic: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting clinic: ${error.message}`);
    }
  },
  // Upload profile photo independently
  async uploadProfilePhoto(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('module_id', '1');
    formData.append('pathLocation', 'PROFILE_IMAGES/');

    // Debug FormData contents
    console.log('FormData contents for uploadProfilePhoto:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value instanceof File ? value.name : value}`);
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/awsS3FileUpload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload profile photo: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error uploading profile photo: ${error.message}`);
    }
  },

  async getAllClinics() {
    try {
      const adminId = Cookies.get('adminid') || localStorage.getItem('adminid');
      if (!adminId) {
        throw new Error('Admin ID not found in cookies');
      }
      // const response = await fetch(`${API_BASE_URL}/admingetclinicdetails`);
      const response = await apiFetch(`${API_BASE_URL}/admingetclinicdetails/${adminId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch clinics: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching clinics: ${error.message}`);
    }
  },

  async getClinicCreationStatus(adminId) {

    const payload = {
      adminId: adminId
    };
    try {
      const response = await apiFetch(`${API_BASE_URL}/getAdminPaymentStatus`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete clinic: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error deleting clinic: ${error.message}`);
    }
  },

  async getPaymentCredentials() {
    try {
      const response = await apiFetch(`${API_BASE_URL}/getPaymentcredentials`, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch payment credentials: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error fetching payment credentials: ${error.message}`);
    }
  },

  async createOrder(payAmount, payCurrency, clinicCount) {
    // SECURITY: never send Razorpay secret from the browser. The server loads it from the DB.
    const payload = {
      payAmount,
      payCurrency,
      clinicCount
    };
    try {
      const response = await apiFetch(`${API_BASE_URL}/create-order`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create order: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error creating order: ${error.message}`);
    }
  },

  async verifyPayment(orderId, adminId) {
    // SECURITY: server verifies with its own credentials; no secret from the client.
    const payload = {
      orderId,
      adminId
    };
    try {
      const response = await apiFetch(`${API_BASE_URL}/verify-payment`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to verify payment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error verifying payment: ${error.message}`);
    }
  },

};

export default ClinicService;